import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { DataTableProvider } from "components/DataTable";
import { PAGINATION_TYPE } from "components/Pagination";
import { FormattedMessage } from "react-intl";
import { QUESTION_TYPE_CONFIGS } from "constants/index";
import Status from "components/Status";
import { shortenText } from "utils";
import { colors, fontSize } from "style";
import { Toggle } from "components/Form";
import { TOGGLE_SIZE } from "components/Form/Toggle";

/**
 * QuestionsList
 *
 * @param {Array}     data
 * @param {Boolean}   loading
 * @param {Number}    resultsPerPage
 * @param {Function}  onToggleQuestion
 * @param {String}    assessmentId
 * @param {Boolean}   canEdit
 */
const QuestionsList = ({
  data = [],
  loading = false,
  resultsPerPage = 20,
  onToggleQuestion = null,
  assessmentId = null,
  canEdit = false,
  ...props
}) => {
  const columns = useMemo(
    () => COLUMN_DATA(onToggleQuestion, assessmentId, canEdit, data),
    [onToggleQuestion, assessmentId, canEdit, data]
  );

  return (
    <DataTableProvider
      keyName="questions"
      loading={loading}
      data={data}
      resultsPerPage={resultsPerPage}
      columns={columns}
      paginationType={PAGINATION_TYPE.classic}
      paginationProps={{
        countMessage: "Global.Questions.Count",
      }}
      {...props}
    />
  );
};

const COLUMN_DATA = (onToggleQuestion, assessmentId, canEdit, data) => [
  {
    cell: (question) => (
      <Toggle
        name={`question-${question.id}`}
        value={question?.properties?.enabled}
        onChange={() => onToggleQuestion?.(question, assessmentId)}
        disabled={!canEdit}
        size={TOGGLE_SIZE.small}
      />
    ),
    width: "60px",
  },
  {
    label: <FormattedMessage id="Missions.Roles.QuestionsList.Wording" />,
    cell: ({ id, wording, properties, type, options }) => {
      const isEnabled = properties?.enabled;

      const enabledQuestions = data.filter(({ properties }) => properties?.enabled);
      const displayIndex = isEnabled ? enabledQuestions.findIndex((question) => question.id === id) + 1 : null;
      const summary = QUESTION_TYPE_CONFIGS[type]?.summary?.({ properties, options });

      return (
        <>
          <div css={styles.title}>
            <FormattedMessage
              id={`Missions.Roles.QuestionsList.Title${isEnabled ? "" : "WithoutIndex"}`}
              values={{ index: displayIndex, title: shortenText(wording, 120) }}
            />
          </div>
          {summary && (
            <div css={styles.subtitle}>
              <FormattedMessage {...summary} />
            </div>
          )}
        </>
      );
    },
    width: "auto",
  },
  {
    label: <FormattedMessage id="Missions.Roles.QuestionsList.Type" />,
    cell: ({ type }) => QUESTION_TYPE_CONFIGS[type]?.label,
    width: "15%",
  },
  {
    label: <FormattedMessage id="Missions.Roles.QuestionsList.Optionality" />,
    cell: ({ properties }) => (
      <Status enabled={properties?.required}>
        <FormattedMessage id={`Global.${properties?.required ? "Yes" : "No"}`} />
      </Status>
    ),
    width: "15%",
  },
];

const styles = {
  title: css`
    font-weight: 400;
  `,
  subtitle: css`
    font-weight: 400;
    padding-top: 3px;
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
  `,
};

QuestionsList.propTypes = {
  resultsPerPage: PropTypes.number,
  loading: PropTypes.bool,
  data: PropTypes.array,
  onToggleQuestion: PropTypes.func,
  assessmentId: PropTypes.string,
  canEdit: PropTypes.bool,
};

export default QuestionsList;
